// Mock service for development and demo mode
// Provides realistic sample data for UI testing

import { 
  MOCK_RECIPES, 
  MOCK_RECIPE_DETAILS, 
  MOCK_UI_SCHEMAS, 
  getPractices, 
  getRecipeDetail as getMockRecipeDetail,
  getDisplayName 
} from './mockRecipes';

// Practices with nested workflows (matching real API structure - derived from actual recipes)
const DEMO_PRACTICES_WITH_WORKFLOWS = [
  { 
    id: 'corporate', 
    name: 'Corporate', 
    description: 'Corporate law and business transactions', 
    icon: 'business',
    workflows: [
      { 
        id: 'nda-analysis', 
        name: 'NDA Analysis', 
        description: 'Analyzes NDAs for affiliate binding and standstill provisions',
        ui: { displayName: 'NDA Analysis' },
        status: 'active',
        estimatedTime: '5-10 minutes'
      },
      { 
        id: 'power-purchase', 
        name: 'Power Purchase Agreement', 
        description: 'Extracts key features from power purchase agreements',
        ui: { displayName: 'Power Purchase Agreement' },
        status: 'active',
        estimatedTime: '5-10 minutes'
      },
      { 
        id: 'horizon-step-one', 
        name: 'Horizon Analysis', 
        description: 'Corporate transaction document analysis (multi-step)',
        ui: { displayName: 'Horizon Analysis' },
        status: 'active',
        estimatedTime: '15-30 minutes'
      },
      { 
        id: 'airplane-lease-step-one', 
        name: 'Aircraft Lease Analysis', 
        description: 'Extraction and analysis of aircraft lease agreements',
        ui: { displayName: 'Aircraft Lease Analysis' },
        status: 'active',
        estimatedTime: '10-15 minutes'
      },
    ]
  },
  { 
    id: 'litigation', 
    name: 'Litigation', 
    description: 'Civil and commercial litigation', 
    icon: 'gavel',
    workflows: [
      { 
        id: 'complaint-summary', 
        name: 'Complaint Summary', 
        description: 'Summarizes legal complaints with parties, claims, events, and witnesses',
        ui: { displayName: 'Complaint Summary' },
        status: 'active',
        estimatedTime: '5-10 minutes'
      },
      { 
        id: 'depo-digest', 
        name: 'Deposition Digest', 
        description: 'Creates digestible summaries of deposition transcripts',
        ui: { displayName: 'Deposition Digest' },
        status: 'active',
        estimatedTime: '10-15 minutes'
      },
      { 
        id: 'pairwise-comparison', 
        name: 'Pairwise Comparison', 
        description: 'Compares documents pairwise to identify inconsistencies',
        ui: { displayName: 'Pairwise Comparison' },
        status: 'active',
        estimatedTime: '5-10 minutes'
      },
      { 
        id: 'cross-doc-inconsistency', 
        name: 'Cross-Document Inconsistency', 
        description: 'Identifies inconsistencies across multiple documents',
        ui: { displayName: 'Cross-Document Inconsistency' },
        status: 'active',
        estimatedTime: '10-15 minutes'
      },
    ]
  },
  { 
    id: 'patent', 
    name: 'Patent', 
    description: 'Patent analysis and litigation support', 
    icon: 'lightbulb',
    workflows: [
      { 
        id: 'section-101-analysis', 
        name: 'Section 101 Analysis', 
        description: 'Analyzes patent claims for Section 101 eligibility',
        ui: { displayName: 'Section 101 Analysis' },
        status: 'active',
        estimatedTime: '10-15 minutes'
      },
      { 
        id: 'rfp', 
        name: 'Request for Production', 
        description: 'Generates RFP responses for patent litigation',
        ui: { displayName: 'Request for Production' },
        status: 'active',
        estimatedTime: '15-20 minutes'
      },
    ]
  },
  { 
    id: 'trademark', 
    name: 'Trademark', 
    description: 'Trademark and brand protection', 
    icon: 'verified',
    workflows: [
      { 
        id: 'ttab', 
        name: 'TTAB Analysis', 
        description: 'Trademark Trial and Appeal Board case analysis',
        ui: { displayName: 'TTAB Analysis' },
        status: 'active',
        estimatedTime: '10-15 minutes'
      },
    ]
  },
  { 
    id: 'real-estate', 
    name: 'Real Estate', 
    description: 'Commercial and residential real estate', 
    icon: 'home',
    workflows: [
      { 
        id: 'lease-abstraction', 
        name: 'Lease Abstraction', 
        description: 'Extracts key terms from commercial leases into Excel',
        ui: { displayName: 'Lease Abstraction' },
        status: 'active',
        estimatedTime: '8-12 minutes'
      },
      { 
        id: 'ocean-bank-step-one', 
        name: 'Ocean Bank Analysis', 
        description: 'Real estate transaction document processing (multi-step)',
        ui: { displayName: 'Ocean Bank Analysis' },
        status: 'active',
        estimatedTime: '15-25 minutes'
      },
      { 
        id: 'loan-commitment', 
        name: 'Loan Commitment', 
        description: 'Analyzes loan commitment letters and documentation',
        ui: { displayName: 'Loan Commitment Analysis' },
        status: 'active',
        estimatedTime: '10-15 minutes'
      },
      {
        id: 'cortese-list',
        name: 'Cortese List',
        description: 'Checks whether a list of addresses appear in one or more Cortese List databases.',
        ui: { displayName: 'Cortese List' },
        status: 'active',
        estimatedTime: '1-2 minutes'
      },
    ]
  },
  { 
    id: 'trial-prep', 
    name: 'Trial Prep', 
    description: 'Trial preparation and case strategy', 
    icon: 'gavel',
    workflows: [
      { 
        id: 'testimony', 
        name: 'Testimony Analysis', 
        description: 'Analyzes and organizes witness testimony for trial',
        ui: { displayName: 'Testimony Analysis' },
        status: 'active',
        estimatedTime: '15-20 minutes'
      },
    ]
  },
];

const DEMO_SIDEBAR_FORMS = {
  forms: [
    { id: 'intake-form', title: 'Client Intake Form', type: 'intake' },
    { id: 'conflict-check', title: 'Conflict Check Request', type: 'compliance' },
    { id: 'matter-open', title: 'New Matter Opening', type: 'matter' },
  ],
  total: 3
};

// Realistic mock jobs using actual recipes and realistic file structures
const DEMO_JOBS = [
  // Succeeded - Corporate NDA Analysis (completed 2 hours ago)
  {
    jobId: 'job-nda-001',
    executionArn: 'arn:aws:states:us-east-1:123456789:execution:mrfusion:job-nda-001',
    displayName: 'Project Atlas - NDA Bundle Review',
    status: 'SUCCEEDED',
    step: 5,
    createdAt: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
    startedAt: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
    endedAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    practice: 'Corporate',
    workflows: ['NDA Analysis'],
    input: {
      practice: 'corporate',
      workflows: ['nda-analysis'],
      number: 'CORP-2026-00142',
      input_file_s3_uris: [
        's3://mrfusion-uploads/corporate/nda-analysis/atlas-mutual-nda-signed.pdf',
        's3://mrfusion-uploads/corporate/nda-analysis/atlas-unilateral-nda-draft.pdf',
        's3://mrfusion-uploads/corporate/nda-analysis/atlas-employee-nda-template.pdf'
      ]
    },
    output: {
      files: [
        { key: 'output/nda-analysis-report.xlsx', name: 'NDA Analysis Report.xlsx', size: 145200, type: 'xlsx' },
        { key: 'output/affiliate-binding-summary.pdf', name: 'Affiliate Binding Summary.pdf', size: 89400, type: 'pdf' },
        { key: 'output/extracted-provisions.json', name: 'Extracted Provisions.json', size: 24680, type: 'json' }
      ],
      summary: { documentsProcessed: 3, issuesFound: 2, completionTime: '4m 12s' }
    }
  },
  // Running - Aircraft Lease Analysis (started 25 min ago)
  {
    jobId: 'job-lease-002',
    executionArn: 'arn:aws:states:us-east-1:123456789:execution:mrfusion:job-lease-002',
    displayName: 'N742WB Boeing 787 Lease Review',
    status: 'RUNNING',
    step: 2,
    createdAt: new Date(Date.now() - 25 * 60 * 1000).toISOString(),
    startedAt: new Date(Date.now() - 25 * 60 * 1000).toISOString(),
    practice: 'Corporate',
    workflows: ['Aircraft Lease Analysis'],
    input: {
      practice: 'corporate',
      workflows: ['airplane-lease-step-one'],
      number: 'AVN-2026-00089',
      input_file_s3_uris: [
        's3://mrfusion-uploads/corporate/airplane-lease/n742wb-operating-lease.pdf',
        's3://mrfusion-uploads/corporate/airplane-lease/n742wb-maintenance-reserve.pdf'
      ]
    }
  },
  // Succeeded - Litigation Complaint Summary (completed yesterday)
  {
    jobId: 'job-lit-003',
    executionArn: 'arn:aws:states:us-east-1:123456789:execution:mrfusion:job-lit-003',
    displayName: 'Chen v. TechStart Inc - Complaint Analysis',
    status: 'SUCCEEDED',
    step: 5,
    createdAt: new Date(Date.now() - 26 * 60 * 60 * 1000).toISOString(),
    startedAt: new Date(Date.now() - 26 * 60 * 60 * 1000).toISOString(),
    endedAt: new Date(Date.now() - 25 * 60 * 60 * 1000).toISOString(),
    practice: 'Litigation',
    workflows: ['Complaint Summary'],
    input: {
      practice: 'litigation',
      workflows: ['complaint-summary'],
      number: 'LIT-2026-00234',
      input_file_s3_uris: [
        's3://mrfusion-uploads/litigation/complaint-summary/chen-v-techstart-complaint.pdf',
        's3://mrfusion-uploads/litigation/complaint-summary/chen-v-techstart-exhibits-a-f.pdf'
      ]
    },
    output: {
      files: [
        { key: 'output/complaint-summary.docx', name: 'Complaint Summary.docx', size: 67800, type: 'docx' },
        { key: 'output/parties-and-claims.xlsx', name: 'Parties and Claims Matrix.xlsx', size: 34500, type: 'xlsx' },
        { key: 'output/key-events-timeline.pdf', name: 'Key Events Timeline.pdf', size: 156200, type: 'pdf' }
      ],
      summary: { documentsProcessed: 2, issuesFound: 0, completionTime: '6m 45s' }
    }
  },
  // Running - Deposition Digest (started 12 min ago)
  {
    jobId: 'job-depo-004',
    executionArn: 'arn:aws:states:us-east-1:123456789:execution:mrfusion:job-depo-004',
    displayName: 'Williams Deposition - Volume 3',
    status: 'RUNNING',
    step: 3,
    createdAt: new Date(Date.now() - 12 * 60 * 1000).toISOString(),
    startedAt: new Date(Date.now() - 12 * 60 * 1000).toISOString(),
    practice: 'Litigation',
    workflows: ['Deposition Digest'],
    input: {
      practice: 'litigation',
      workflows: ['depo-digest'],
      number: 'LIT-2026-00198',
      input_file_s3_uris: [
        's3://mrfusion-uploads/litigation/depo-digest/williams-depo-vol3-transcript.pdf'
      ]
    }
  },
  // Queued - Patent Section 101 Analysis
  {
    jobId: 'job-pat-005',
    executionArn: 'arn:aws:states:us-east-1:123456789:execution:mrfusion:job-pat-005',
    displayName: 'US 11,234,567 - Eligibility Review',
    status: 'QUEUED',
    step: 0,
    createdAt: new Date(Date.now() - 3 * 60 * 1000).toISOString(),
    practice: 'Patent',
    workflows: ['Section 101 Analysis'],
    input: {
      practice: 'patent',
      workflows: ['section-101-analysis'],
      number: 'PAT-2026-00456',
      input_file_s3_uris: [
        's3://mrfusion-uploads/patent/section-101/us-11234567-patent.pdf',
        's3://mrfusion-uploads/patent/section-101/us-11234567-claims.pdf'
      ]
    }
  },
  // Succeeded - Real Estate Lease Abstraction (completed 2 days ago)
  {
    jobId: 'job-re-006',
    executionArn: 'arn:aws:states:us-east-1:123456789:execution:mrfusion:job-re-006',
    displayName: '500 Park Ave - Commercial Lease Portfolio',
    status: 'SUCCEEDED',
    step: 5,
    createdAt: new Date(Date.now() - 52 * 60 * 60 * 1000).toISOString(),
    startedAt: new Date(Date.now() - 52 * 60 * 60 * 1000).toISOString(),
    endedAt: new Date(Date.now() - 51 * 60 * 60 * 1000).toISOString(),
    practice: 'Real Estate',
    workflows: ['Lease Abstraction'],
    input: {
      practice: 'real-estate',
      workflows: ['lease-abstraction'],
      number: 'RE-2026-00078',
      input_file_s3_uris: [
        's3://mrfusion-uploads/real-estate/lease-abstraction/500-park-floor-12-lease.pdf',
        's3://mrfusion-uploads/real-estate/lease-abstraction/500-park-floor-14-lease.pdf',
        's3://mrfusion-uploads/real-estate/lease-abstraction/500-park-floor-15-lease.pdf',
        's3://mrfusion-uploads/real-estate/lease-abstraction/500-park-retail-lease.pdf'
      ]
    },
    output: {
      files: [
        { key: 'output/lease-abstracts.xlsx', name: 'Lease Abstracts Summary.xlsx', size: 234500, type: 'xlsx' },
        { key: 'output/key-dates-calendar.ics', name: 'Key Dates Calendar.ics', size: 8900, type: 'ics' },
        { key: 'output/rent-escalation-schedule.xlsx', name: 'Rent Escalation Schedule.xlsx', size: 45600, type: 'xlsx' }
      ],
      summary: { documentsProcessed: 4, issuesFound: 1, completionTime: '12m 34s' }
    }
  },
  // Failed - Cross-Document Inconsistency (failed 4 hours ago)
  {
    jobId: 'job-lit-007',
    executionArn: 'arn:aws:states:us-east-1:123456789:execution:mrfusion:job-lit-007',
    displayName: 'Discovery Batch - Inconsistency Check',
    status: 'FAILED',
    step: 3,
    createdAt: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
    startedAt: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
    endedAt: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
    practice: 'Litigation',
    workflows: ['Cross-Document Inconsistency'],
    error: 'Document extraction failed: Unable to parse scanned image on page 47. Please ensure documents are OCR-processed.',
    input: {
      practice: 'litigation',
      workflows: ['cross-doc-inconsistency'],
      number: 'LIT-2026-00201',
      input_file_s3_uris: [
        's3://mrfusion-uploads/litigation/cross-doc/discovery-batch-1.pdf',
        's3://mrfusion-uploads/litigation/cross-doc/discovery-batch-2.pdf',
        's3://mrfusion-uploads/litigation/cross-doc/discovery-batch-3.pdf'
      ]
    }
  },
  // Waiting for Input - Ocean Bank Analysis (human-in-the-loop)
  {
    jobId: 'job-ocean-008',
    executionArn: 'arn:aws:states:us-east-1:123456789:execution:mrfusion:job-ocean-008',
    displayName: 'Marina Tower Refinance - Document Review',
    status: 'WAITING_FOR_INPUT',
    step: 3,
    createdAt: new Date(Date.now() - 45 * 60 * 1000).toISOString(),
    startedAt: new Date(Date.now() - 45 * 60 * 1000).toISOString(),
    practice: 'Real Estate',
    workflows: ['Ocean Bank Analysis'],
    input: {
      practice: 'real-estate',
      workflows: ['ocean-bank-step-two'],
      number: 'RE-2026-00092',
      input_file_s3_uris: [
        's3://mrfusion-uploads/real-estate/ocean-bank/marina-tower-loan-docs.pdf',
        's3://mrfusion-uploads/real-estate/ocean-bank/marina-tower-title-report.pdf'
      ]
    },
    humanInput: {
      promptMessage: 'Please verify the borrower entity name. The document shows both "Marina Tower Holdings LLC" and "Marina Holdings LLC". Which is correct?',
      options: ['Marina Tower Holdings LLC', 'Marina Holdings LLC', 'Both are valid (DBA)']
    }
  },
  // Succeeded - Horizon Corporate Analysis (completed 6 hours ago)
  {
    jobId: 'job-hor-009',
    executionArn: 'arn:aws:states:us-east-1:123456789:execution:mrfusion:job-hor-009',
    displayName: 'Project Sunrise - M&A Document Sort',
    status: 'SUCCEEDED',
    step: 5,
    createdAt: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString(),
    startedAt: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString(),
    endedAt: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
    practice: 'Corporate',
    workflows: ['Horizon Analysis'],
    input: {
      practice: 'corporate',
      workflows: ['horizon-step-one'],
      number: 'CORP-2026-00156',
      input_file_s3_uris: [
        's3://mrfusion-uploads/corporate/horizon/sunrise-data-room-batch-1.zip',
        's3://mrfusion-uploads/corporate/horizon/sunrise-data-room-batch-2.zip'
      ]
    },
    output: {
      files: [
        { key: 'output/document-classification.xlsx', name: 'Document Classification Index.xlsx', size: 567800, type: 'xlsx' },
        { key: 'output/category-summaries.pdf', name: 'Category Summaries.pdf', size: 234500, type: 'pdf' },
        { key: 'output/red-flags-report.docx', name: 'Red Flags Report.docx', size: 89200, type: 'docx' },
        { key: 'output/full-extraction.json', name: 'Full Extraction Data.json', size: 1245000, type: 'json' }
      ],
      summary: { documentsProcessed: 147, issuesFound: 12, completionTime: '1h 52m' }
    }
  },
  // Running - TTAB Trademark Analysis (started 8 min ago)
  {
    jobId: 'job-tm-010',
    executionArn: 'arn:aws:states:us-east-1:123456789:execution:mrfusion:job-tm-010',
    displayName: 'APEX v. APEXTECH - Opposition Analysis',
    status: 'RUNNING',
    step: 2,
    createdAt: new Date(Date.now() - 8 * 60 * 1000).toISOString(),
    startedAt: new Date(Date.now() - 8 * 60 * 1000).toISOString(),
    practice: 'Trademark',
    workflows: ['TTAB Analysis'],
    input: {
      practice: 'trademark',
      workflows: ['ttab'],
      number: 'TM-2026-00034',
      input_file_s3_uris: [
        's3://mrfusion-uploads/trademark/ttab/apex-opposition-petition.pdf',
        's3://mrfusion-uploads/trademark/ttab/apextech-registration-certificate.pdf',
        's3://mrfusion-uploads/trademark/ttab/apex-use-specimens.pdf'
      ]
    }
  },
  // Succeeded - Power Purchase Agreement (completed 3 days ago)
  {
    jobId: 'job-ppa-011',
    executionArn: 'arn:aws:states:us-east-1:123456789:execution:mrfusion:job-ppa-011',
    displayName: 'SunField Solar - 25yr PPA Review',
    status: 'SUCCEEDED',
    step: 5,
    createdAt: new Date(Date.now() - 75 * 60 * 60 * 1000).toISOString(),
    startedAt: new Date(Date.now() - 75 * 60 * 60 * 1000).toISOString(),
    endedAt: new Date(Date.now() - 74 * 60 * 60 * 1000).toISOString(),
    practice: 'Corporate',
    workflows: ['Power Purchase Agreement'],
    input: {
      practice: 'corporate',
      workflows: ['power-purchase'],
      number: 'ENERGY-2026-00023',
      input_file_s3_uris: [
        's3://mrfusion-uploads/corporate/power-purchase/sunfield-ppa-executed.pdf',
        's3://mrfusion-uploads/corporate/power-purchase/sunfield-ppa-amendment-1.pdf'
      ]
    },
    output: {
      files: [
        { key: 'output/ppa-term-sheet.xlsx', name: 'PPA Key Terms Summary.xlsx', size: 78900, type: 'xlsx' },
        { key: 'output/pricing-analysis.xlsx', name: 'Pricing & Escalation Analysis.xlsx', size: 45600, type: 'xlsx' },
        { key: 'output/risk-allocation-memo.pdf', name: 'Risk Allocation Memo.pdf', size: 123400, type: 'pdf' }
      ],
      summary: { documentsProcessed: 2, issuesFound: 0, completionTime: '8m 15s' }
    }
  },
  // Queued - Testimony Analysis
  {
    jobId: 'job-trial-012',
    executionArn: 'arn:aws:states:us-east-1:123456789:execution:mrfusion:job-trial-012',
    displayName: 'State v. Morrison - Expert Testimony Prep',
    status: 'QUEUED',
    step: 0,
    createdAt: new Date(Date.now() - 1 * 60 * 1000).toISOString(),
    practice: 'Trial Prep',
    workflows: ['Testimony Analysis'],
    input: {
      practice: 'trial-prep',
      workflows: ['testimony'],
      number: 'TRIAL-2026-00067',
      input_file_s3_uris: [
        's3://mrfusion-uploads/trial-prep/testimony/morrison-expert-report-smith.pdf',
        's3://mrfusion-uploads/trial-prep/testimony/morrison-expert-report-jones.pdf',
        's3://mrfusion-uploads/trial-prep/testimony/morrison-prior-testimony.pdf'
      ]
    }
  },
  // Succeeded - Cortese List (has matches) — demoes the address-search results UI
  {
    jobId: 'job-cortese-013',
    executionArn: 'arn:aws:states:us-east-1:123456789:execution:mrfusion:job-cortese-013',
    displayName: '07-23_9659',
    status: 'SUCCEEDED',
    step: 1,
    createdAt: new Date(Date.now() - 20 * 60 * 1000).toISOString(),
    startedAt: new Date(Date.now() - 20 * 60 * 1000).toISOString(),
    endedAt: new Date(Date.now() - 20 * 60 * 1000 + 5000).toISOString(),
    practice: 'real-estate',
    workflows: ['cortese-list'],
    input: {
      practice: 'real-estate',
      workflows: ['cortese-list'],
      number: '07-23_9659',
      input_file_s3_uris: [],
      workflow_data: {
        addresses: [
          '11 Marina Blvd, San Francisco CA 94123',
          '3130 Penryn Road, Placer County CA 95663',
          '1000 S. Fremont Avenue, Alhambra CA 91803',
          '12825 Ventura Blvd, Los Angeles CA 91604'
        ]
      }
    },
    output: {
      files: [],
      summary: { documentsProcessed: 0, issuesFound: 1, completionTime: '5s' },
      workflow_data: {
        addresses: [
          '11 Marina Blvd, San Francisco CA 94123',
          '3130 Penryn Road, Placer County CA 95663',
          '1000 S. Fremont Avenue, Alhambra CA 91803',
          '12825 Ventura Blvd, Los Angeles CA 91604'
        ],
        cortese_list: {
          count: 1,
          summary: '1 match(es):\n\n- [geotracker] 1000 SOUTH FREMONT — Completed - Case Closed',
          source_downloaded: '2026-07-23',
          matches: [
            {
              source: 'geotracker',
              query_address: '1000 S. Fremont Avenue, Alhambra CA 91803',
              site_name: '1000 SOUTH FREMONT',
              status: 'Completed - Case Closed',
              city: 'ALHAMBRA',
              county: 'Los Angeles',
              latitude: '34.081873',
              longitude: '-118.150256',
              global_id: 'T10000001005'
            }
          ]
        }
      }
    }
  },
  // Succeeded - Cortese List (no matches) — demoes the "all clear" state
  {
    jobId: 'job-cortese-014',
    executionArn: 'arn:aws:states:us-east-1:123456789:execution:mrfusion:job-cortese-014',
    displayName: '07-23_0560',
    status: 'SUCCEEDED',
    step: 1,
    createdAt: new Date(Date.now() - 40 * 60 * 1000).toISOString(),
    startedAt: new Date(Date.now() - 40 * 60 * 1000).toISOString(),
    endedAt: new Date(Date.now() - 40 * 60 * 1000 + 4000).toISOString(),
    practice: 'real-estate',
    workflows: ['cortese-list'],
    input: {
      practice: 'real-estate',
      workflows: ['cortese-list'],
      number: '07-23_0560',
      input_file_s3_uris: [],
      workflow_data: {
        addresses: ['101 Oyster Point Blvd, South San Francisco CA 94080']
      }
    },
    output: {
      files: [],
      summary: { documentsProcessed: 0, issuesFound: 0, completionTime: '4s' },
      workflow_data: {
        addresses: ['101 Oyster Point Blvd, South San Francisco CA 94080'],
        cortese_list: {
          count: 0,
          summary: 'No matches found.',
          matches: []
        }
      }
    }
  },
];

// Demo RBAC permissions - admin access for demo user
const DEMO_RBAC = {
  roles: ['admin', 'user'],
  permissions: [
    { resource: '*', actions: ['*'] },
    { resource: 'workflows', actions: ['create', 'read', 'update', 'delete', 'execute'] },
    { resource: 'jobs', actions: ['create', 'read', 'update', 'delete'] },
    { resource: 'files', actions: ['create', 'read', 'update', 'delete'] },
    { resource: 'chat', actions: ['create', 'read'] },
    { resource: 'admin', actions: ['read', 'write'] },
    { resource: 'recipes', actions: ['create', 'read', 'update', 'delete', 'sync'] },
    { resource: 'users', actions: ['read', 'update'] },
    { resource: 'forms', actions: ['create', 'read', 'update', 'delete'] },
  ]
};

export const mockService = {
  getFileContent: async (): Promise<string> => 'Sample document content for demo purposes.',
  
  getPractices: async () => DEMO_PRACTICES_WITH_WORKFLOWS,
  
  getWorkflows: async () => {
    // Flatten all workflows from all practices
    return DEMO_PRACTICES_WITH_WORKFLOWS.flatMap(p => 
      p.workflows.map(w => ({ ...w, practiceId: p.id }))
    );
  },
  
  // When practiceId is empty, return all practices; otherwise return specific practice
  getPracticeDetails: async (practiceId?: string) => {
    if (!practiceId) {
      return DEMO_PRACTICES_WITH_WORKFLOWS;
    }
    const practice = DEMO_PRACTICES_WITH_WORKFLOWS.find(p => p.id === practiceId);
    return practice || DEMO_PRACTICES_WITH_WORKFLOWS[0];
  },
  
  getWorkflowsForPractice: async (practiceId: string) => {
    const practice = DEMO_PRACTICES_WITH_WORKFLOWS.find(p => p.id === practiceId);
    return practice?.workflows || [];
  },

  getSidebarForms: async () => DEMO_SIDEBAR_FORMS,
  
  uploadFiles: async (files: File[]) => ({ 
    s3Uris: files.map((f, i) => `s3://demo-bucket/uploads/${f.name}`), 
    count: files.length 
  }),
  
  executeWorkflow: async (workflowId: string) => ({ 
    executionArn: `arn:aws:states:us-east-1:demo:execution:${workflowId}:${Date.now()}`, 
    status: 'RUNNING' 
  }),
  
  getExecutionStatus: async (executionArn: string) => {
    // Find the job by execution ARN
    const job = DEMO_JOBS.find(j => j.executionArn === executionArn);
    if (job) {
      // Parse completionTime string to seconds (e.g., "4m 12s" -> 252, "1h 52m" -> 6720)
      const parseTimeToSeconds = (timeStr: string): number => {
        if (!timeStr) return 0;
        let seconds = 0;
        const hourMatch = timeStr.match(/(\d+)h/);
        const minMatch = timeStr.match(/(\d+)m/);
        const secMatch = timeStr.match(/(\d+)s/);
        if (hourMatch) seconds += parseInt(hourMatch[1]) * 3600;
        if (minMatch) seconds += parseInt(minMatch[1]) * 60;
        if (secMatch) seconds += parseInt(secMatch[1]);
        return seconds;
      };

      const output = (job as any).output;
      const inputFiles = job.input?.input_file_s3_uris || [];
      
      return {
        status: job.status,
        step: job.step,
        progress: job.status === 'SUCCEEDED' ? 100 : Math.round((job.step / 5) * 100),
        currentStep: job.status === 'RUNNING' ? 'Processing documents...' : undefined,
        error: (job as any).error,
        humanInput: (job as any).humanInput,
        startDate: job.startedAt,
        stopDate: (job as any).endedAt,
        input: job.input,
        output: output ? {
          output_files: output.files?.map((f: any) => ({
            name: f.name,
            s3_uri: `s3://mrfusion-outputs/${executionArn}/${f.key}`,
            size: f.size
          })) || [],
          // pass through any data-only V2 step outputs (e.g. cortese_list matches)
          ...(output.workflow_data ? { workflow_data: output.workflow_data } : {})
        } : undefined
      };
    }
    return {
      status: 'RUNNING',
      progress: Math.floor(Math.random() * 100),
      currentStep: 'Processing documents...'
    };
  },
  
  getExecutionResults: async (executionArn?: string) => {
    // Parse completionTime string to seconds
    const parseTimeToSeconds = (timeStr: string): number => {
      if (!timeStr) return 0;
      let seconds = 0;
      const hourMatch = timeStr.match(/(\d+)h/);
      const minMatch = timeStr.match(/(\d+)m/);
      const secMatch = timeStr.match(/(\d+)s/);
      if (hourMatch) seconds += parseInt(hourMatch[1]) * 3600;
      if (minMatch) seconds += parseInt(minMatch[1]) * 60;
      if (secMatch) seconds += parseInt(secMatch[1]);
      return seconds;
    };

    // Find the job by execution ARN and return its output
    if (executionArn) {
      const job = DEMO_JOBS.find(j => j.executionArn === executionArn);
      if (job && (job as any).output) {
        const output = (job as any).output;
        const inputFiles = job.input?.input_file_s3_uris || [];
        return {
          files: output.files || [],
          summary: {
            processingTime: parseTimeToSeconds(output.summary?.completionTime),
            documentsProcessed: inputFiles.length,
            outputFilesGenerated: (output.files || []).length,
            issuesFound: output.summary?.issuesFound || 0,
          }
        };
      }
    }
    // Fallback for jobs without specific output
    return { 
      files: [
        { key: 'output/analysis-report.pdf', name: 'Analysis Report.pdf', size: 245000, type: 'pdf' },
        { key: 'output/extracted-data.json', name: 'Extracted Data.json', size: 12400, type: 'json' },
      ], 
      summary: {
        processingTime: 272,
        documentsProcessed: 12,
        outputFilesGenerated: 2,
        issuesFound: 3,
      }
    };
  },
  
  getJobById: async (jobId: string) => {
    return DEMO_JOBS.find(j => j.jobId === jobId) || null;
  },
  
  getSignedDownloadUrl: async (key: string): Promise<string> => `https://demo-signed-url.example.com/${key}`,
  
  submitUserInput: async () => ({ success: true, message: 'Input submitted successfully' }),
  
  getAllJobs: async () => DEMO_JOBS,
  
  pauseJob: async (jobId: string) => ({ success: true, jobId, status: 'paused' }),
  
  resumeJob: async (jobId: string) => ({ success: true, jobId, status: 'running' }),
  
  cancelJob: async (jobId: string) => ({ success: true, jobId, status: 'cancelled' }),
  
  renameJob: async (jobId: string, newName: string) => ({ success: true, jobId, name: newName }),
  
  deleteJob: async (jobId: string) => ({ success: true, jobId }),
  
  getRecipeRequirements: async (recipeId: string, workflow?: string) => {
    // cortese-list takes address text input (no files) and produces inline match data.
    // WorkflowPage calls this as getRecipeRequirements(practice, workflow), so check both args.
    const key = `${recipeId || ''} ${workflow || ''}`;
    if (key.includes('cortese')) {
      return {
        description: 'Checks whether a list of addresses appear in one or more Cortese List databases.',
        display_name: 'Cortese List',
        processing_type: 'v2-workflow',
        supported_formats: [],
        required_inputs: [],
        ui_elements: [
          {
            type: 'address_list',
            config: {
              label: 'Street Addresses',
              placeholder: 'e.g. 1000 S. Fremont Avenue, Alhambra',
              required: true,
              help_text: 'Add each street address you want to check against the Cortese List databases. Type an address and press Enter or click Add. To add several at once, paste them one per line.',
              field_name: 'addresses'
            }
          }
        ]
      };
    }
    return {
      description: 'Sample workflow for processing legal documents with AI assistance.',
      supported_formats: ['pdf', 'docx', 'txt', 'xlsx'],
      maxFileSize: '50MB',
      estimatedTime: '5-15 minutes'
    };
  },

  // Demo recipes for Workflow Studio - using actual recipe structures
  getAdminRecipes: async () => ({
    practices: getPractices(),
    recipes: MOCK_RECIPES
  }),

  getRecipeDetail: async (practice: string, workflow: string) => {
    // Return actual recipe detail from mock data
    return getMockRecipeDetail(practice, workflow);
  },

  getRecipeFiles: async (practice: string, workflow: string) => ({
    files: [
      {
        name: 'recipe.json',
        key: `recipes/${practice}/${workflow}/recipe.json`,
        size: 2048,
        lastModified: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
        type: 'recipe'
      },
      {
        name: 'ui-schema.json',
        key: `recipes/${practice}/${workflow}/ui-schema.json`,
        size: 1024,
        lastModified: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
        type: 'uiSchema'
      },
      {
        name: 'extraction-main.txt',
        key: `prompts/${practice}/${workflow}/extraction-main.txt`,
        size: 4096,
        lastModified: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
        type: 'prompt'
      },
      {
        name: 'structure.txt',
        key: `prompts/${practice}/${workflow}/structure.txt`,
        size: 2048,
        lastModified: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
        type: 'structure'
      }
    ]
  })
};
